/**
 * /kid — parent PIN gate, then a full-screen simplified UI for the
 * child: big tiles of ready stories only, tap → a stripped-down player (one
 * huge button, cover, progress, no settings). Exiting back to the parent
 * view asks for the PIN again, via the same small corner button.
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";
import { Pause, Play, X } from "lucide-react";
import { Container, Button, MutedText } from "../components/ui";
import { Cover } from "../components/Cover";
import { ChapterScene } from "../components/ChapterScene";
import { ChapterSceneFullscreen } from "../components/ChapterSceneFullscreen";
import { PinPad } from "../components/PinPad";
import { useChildren } from "../lib/childrenStore";
import { useAuth } from "../lib/auth";
import { useParentGate } from "../lib/parentGate";
import { isPlus } from "../lib/plan";
import { getMyStories, getCatalog, getStory, verifyKidPin } from "../lib/dataSource";
import { useAudioPlayer, type AudioTrack } from "../lib/audioPlayer";
import { svgCoverToDataUrl } from "../lib/coverArtwork";
import { hasAnyChapterImage, toSceneChapters } from "../lib/chapterScenes";
import type { StoryCard, StoryDetail } from "../lib/types";

function PinGate({
  onSuccess,
  titleKey,
  subtitleKey,
}: {
  onSuccess: () => void;
  titleKey: string;
  subtitleKey: string;
}) {
  const { t } = useTranslation();

  async function checkPin(pin: string): Promise<boolean> {
    try {
      const { ok } = await verifyKidPin(pin);
      if (ok) onSuccess();
      return ok;
    } catch {
      // Un échec réseau doit se lire comme un code refusé : le pavé efface
      // sa saisie et affiche l'erreur plutôt que de rester bloqué sur une
      // promesse qui ne se résout jamais côté PinPad.
      return false;
    }
  }

  return (
    <div
      className="safe-top min-h-screen bg-night text-paper flex items-center justify-center px-6 pb-[calc(2rem+env(safe-area-inset-bottom,0px))]"
      style={{ "--safe-top-extra": "2rem" } as React.CSSProperties}
    >
      <PinPad title={t(titleKey)} subtitle={t(subtitleKey)} onSubmit={checkPin} />
    </div>
  );
}

function KidPlayer({ story, onClose }: { story: StoryCard; onClose: () => void }) {
  const { t } = useTranslation();
  const {
    track, isPlaying, currentTime, duration, toggle, currentChapter,
  } = useAudioPlayer();

  const progress = duration > 0 ? currentTime / duration : 0;
  const showScenes =
    !!track && track.id === story.id && hasAnyChapterImage(toSceneChapters(track.chapters));
  const [sceneFullscreen, setSceneFullscreen] = useState(false);
  const expandTriggerRef = useRef<HTMLButtonElement | null>(null);

  return (
    <div
      className="safe-top min-h-screen bg-night text-paper flex flex-col items-center justify-center gap-8 px-8 pb-[calc(2rem+env(safe-area-inset-bottom,0px))]"
      style={{ "--safe-top-extra": "1.5rem" } as React.CSSProperties}
    >
      {/* `fixed`, pas `absolute` : ce conteneur n'est pas positionné, un
       * `absolute` s'y calerait alors sur le bloc initial et non sur cet
       * écran. `safe-fixed-top` plutôt que `top-4` : un élément `fixed` ne
       * profite pas du `padding-top` de `.safe-top` posé au-dessus de lui. */}
      <button
        type="button"
        onClick={onClose}
        className="fixed right-4 safe-fixed-top z-10 tap-target text-violet-soft"
        aria-label={t("common.close")}
      >
        <X size={24} />
      </button>
      <div className="w-64">
        {showScenes && track ? (
          <ChapterScene
            ref={expandTriggerRef}
            track={track}
            currentTime={currentTime}
            rounded="rounded-2xl"
            onExpand={() => setSceneFullscreen(true)}
          />
        ) : (
          <Cover
            svg={story.cover_svg}
            imageUrl={story.cover_image_url}
            imageThumbUrl={story.cover_image_thumb_url}
            imageSize="full"
            title={story.title}
            colorway={story.cover_colorway}
            colorKey={story.slug}
            rounded="rounded-2xl"
          />
        )}
      </div>
      <h1 className="font-heading font-bold text-2xl text-center">{story.title}</h1>
      <div className="w-full max-w-xs h-2 rounded-full bg-line overflow-hidden">
        <div className="h-full bg-coral" style={{ width: `${progress * 100}%` }} />
      </div>
      <button
        type="button"
        onClick={toggle}
        disabled={!track}
        className="tap-target w-28 h-28 rounded-full bg-coral text-mint-ink overprint ink-sun flex items-center justify-center"
      >
        {isPlaying ? <Pause size={44} fill="currentColor" /> : <Play size={44} fill="currentColor" className="ml-1" />}
      </button>

      {showScenes && track && sceneFullscreen && (
        <ChapterSceneFullscreen
          track={track}
          currentTime={currentTime}
          duration={duration}
          chapterTitle={currentChapter?.title ?? story.title}
          isPlaying={isPlaying}
          onTogglePlay={toggle}
          onClose={() => {
            setSceneFullscreen(false);
            expandTriggerRef.current?.focus();
          }}
        />
      )}
    </div>
  );
}

export default function KidMode() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { me } = useAuth();
  const { unlock: unlockParent } = useParentGate();
  const { currentChild } = useChildren();
  const { play } = useAudioPlayer();
  const [unlocked, setUnlocked] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [stories, setStories] = useState<StoryCard[]>([]);
  const [selected, setSelected] = useState<StoryCard | null>(null);
  // Full details (audio_url, chapters) prefetched as soon as the tile grid
  // loads, keyed by story id — so tapping a tile can call `play()`
  // synchronously, in the same event-handler call stack as the tap. iOS
  // Safari only honours `<audio>.play()` as "triggered by a user gesture"
  // when nothing async (a network fetch, here) intervenes between the tap
  // and the call; fetching the detail *after* the tap, then calling `play()`
  // from that promise's `.then()`, silently loses autoplay permission there.
  const [detailsById, setDetailsById] = useState<Record<string, StoryDetail>>({});

  useEffect(() => {
    if (!unlocked || !currentChild) return;
    void Promise.all([getMyStories(currentChild.id), getCatalog(currentChild.id)]).then(([mine, catalog]) => {
      const ready = [...mine, ...catalog.free].filter((s) => s.status === "ready");
      setStories(ready);
      void Promise.all(ready.map((s) => getStory(s.id))).then((details) => {
        setDetailsById(Object.fromEntries(details.map((d) => [d.id, d])));
      });
    });
  }, [unlocked, currentChild]);

  function selectStory(s: StoryCard) {
    const detail = detailsById[s.id];
    if (detail?.audio_url) {
      const track: AudioTrack = {
        id: detail.id,
        url: detail.audio_url,
        title: detail.title,
        coverSvg: detail.cover_svg,
        coverImageUrl: detail.cover_image_url,
        coverImageThumbUrl: detail.cover_image_thumb_url,
        coverColorway: detail.cover_colorway,
        artwork: svgCoverToDataUrl(detail.cover_svg),
        chapters: detail.chapters,
        kind: detail.kind,
      };
      play(track);
    }
    setSelected(s);
  }

  // Filet côté front : le mode enfant fait partie de Chouette+ (D7), alors
  // que le code parent, lui, existe aussi en gratuit — ceci évite au parent
  // gratuit d'atterrir sur l'écran de code d'un mode qu'il n'a pas.
  if (me && !isPlus(me)) {
    return <Navigate to="/plus" replace />;
  }

  if (me && !me.kid_pin_set) {
    return (
      <div className="min-h-screen bg-night text-paper flex items-center justify-center px-6 text-center">
        <Container className="flex flex-col items-center gap-4 max-w-xs">
          <MutedText>{t("kid.setPinFirst")}</MutedText>
          <Button onClick={() => navigate("/settings")}>{t("nav.settings")}</Button>
        </Container>
      </div>
    );
  }

  if (!unlocked) {
    return (
      <PinGate
        titleKey="kid.enterPin"
        subtitleKey="kid.enterPinSubtitle"
        onSuccess={() => setUnlocked(true)}
      />
    );
  }

  if (exiting) {
    return (
      <PinGate
        titleKey="kid.exitPinTitle"
        subtitleKey="kid.exitPinSubtitle"
        onSuccess={() => {
          // Il vient de prouver qu'il est le parent : inutile de lui
          // redemander le code pour les réglages juste après.
          unlockParent();
          navigate("/");
        }}
      />
    );
  }

  if (selected) {
    return <KidPlayer story={selected} onClose={() => setSelected(null)} />;
  }

  return (
    <div className="safe-top min-h-screen bg-night text-paper px-5 pb-10" style={{ "--safe-top-extra": "2rem" } as React.CSSProperties}>
      {/* `safe-fixed-top` : ce bouton est `fixed`, donc calé sur le
       * viewport — le `padding-top` de `.safe-top` posé juste en dessous,
       * sur ce conteneur, ne s'applique pas à lui. */}
      <button
        type="button"
        onClick={() => setExiting(true)}
        className="fixed right-4 safe-fixed-top tap-target text-[11px] font-heading font-semibold text-violet-muted bg-surface border border-line rounded-pill px-3 py-2 overprint overprint-sm ink-line z-10"
      >
        {t("kid.exit")}
      </button>
      <h1 className="headline text-2xl mb-6">{t("kid.greeting", { name: currentChild?.first_name ?? "" })}</h1>
      {stories.length === 0 ? (
        <MutedText>{t("kid.noStories")}</MutedText>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          {stories.map((s) => (
            <button key={s.id} type="button" onClick={() => selectStory(s)} className="flex flex-col gap-2">
              <Cover
                svg={s.cover_svg}
                imageUrl={s.cover_image_url}
                imageThumbUrl={s.cover_image_thumb_url}
                title={s.title}
                colorway={s.cover_colorway}
                colorKey={s.slug}
                rounded="rounded-2xl"
              />
              <span className="font-heading font-bold text-sm text-center leading-tight">{s.title}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
